import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Observable } from "rxjs";
import {
  AddCaxtonCardRequest,
  AssignCardsRequest,
  BulkUpdateCaxtonCardsRequest,
  BulkUpdateCaxtonCardsResult,
  CaxtonCardViewModel,
  ExportCaxtonCardsRequest,
  GetCaxtonCardsRequest,
  UpdateCaxtonCardRequest,
  UpdateCaxtonCardsDeliveryStatusRequest,
  UpdateCaxtonCardsExpiryDateRequest,
  UpdateCaxtonCardsOfficeRequest
} from "../models/card-tracker.model";
import { ResponsePage } from "../models/search-page.model";
import { CreateTestWalletResult, UpdateCaxtonCardStateResult } from "../models/patient.model";

@Injectable({
  providedIn: 'root'
})
export class CaxtonCardService {
  private endpoint = '/caxtoncard';

  constructor(private http: HttpClient) { }

  getLastCardNumber(): Observable<{ no: number }> {
    return this.http.get<{ no: number }>(`${environment.apiUrl}${this.endpoint}/lastcard`);
  }

  addCards(request: AddCaxtonCardRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}${this.endpoint}`, request);
  }

  updateCard(request: UpdateCaxtonCardRequest): Observable<UpdateCaxtonCardStateResult> {
    return this.http.put<UpdateCaxtonCardStateResult>(`${environment.apiUrl}${this.endpoint}`, request);
  }

  updateCardsExpiryDate(request: UpdateCaxtonCardsExpiryDateRequest): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}${this.endpoint}/expiry`, request);
  }

  updateCardsDeliveryStatus(request: UpdateCaxtonCardsDeliveryStatusRequest): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}${this.endpoint}/delivery`, request);
  }

  updateCardsOffice(request: UpdateCaxtonCardsOfficeRequest): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}${this.endpoint}/office`, request);
  }

  assignCards(request: AssignCardsRequest): Observable<void> {
    return this.http.put<void>(`${environment.apiUrl}${this.endpoint}/assign`, request);
  }

  getCaxtonCards(request: GetCaxtonCardsRequest): Observable<ResponsePage<CaxtonCardViewModel>> {
    let params = new HttpParams();
    params = params.set('page', request.page);
    params = params.set('filter', request.filter);
    params = params.set('cardStatus', request.cardStatus);
    params = params.set('office', request.office);
    params = params.set('trialId', request.trialId);
    params = params.set('searchTerm', request.searchTerm);

    if (request.expiryFrom) {
      const fromDate = new Date(request.expiryFrom);
      const firstOfMonth = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1, 0, 0, 0);
      params = params.set('expiryFrom', firstOfMonth.toISOString());
    }

    if (request.expiryTo) {
      const toDate = new Date(request.expiryTo);
      const lastOfMonth = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0, 23, 59, 50);
      params = params.set('expiryTo', lastOfMonth.toISOString());
    }

    return this.http.get<ResponsePage<CaxtonCardViewModel>>(`${environment.apiUrl}${this.endpoint}`, { params: params });
  }

  exportCards(request: ExportCaxtonCardsRequest): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}${this.endpoint}/export`, request);
  }

  bulkUpdateCards(request: BulkUpdateCaxtonCardsRequest): Observable<BulkUpdateCaxtonCardsResult> {
    return this.http.post<BulkUpdateCaxtonCardsResult>(`${environment.apiUrl}${this.endpoint}/bulkupdate`, request);
  }

  archiveEmptyWallets(dryRun: boolean): Observable<void> {
    const params = new HttpParams().set('dryRun', dryRun);
    return this.http.post<void>(`${environment.apiUrl}${this.endpoint}/archive-empty-wallets`, {}, { params: params });
  }

  createTestWalletOnParentSubsidiary(walletName: string): Observable<CreateTestWalletResult> {
    return this.http.post<CreateTestWalletResult>(`${environment.apiUrl}${this.endpoint}/create-test-wallet`, { walletName });
  }
}
